import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface InviteFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const permissionOptions = [
  ['manage_shifts', 'シフト作成・公開'],
  ['approve_attendance', '勤怠確認・承認'],
  ['manage_operations', '担当部門の業務管理'],
  ['send_notifications', 'スタッフ通知'],
  ['view_reports', '担当部門のレポート閲覧'],
] as const;

export default function InviteForm({ onSuccess, onCancel }: InviteFormProps) {
  const { session } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    address: '',
    access_level: 'staff' as 'super_admin' | 'operations_manager' | 'staff',
    department_id: '',
    department_ids: [] as string[],
    permissions: [] as string[],
    default_landing_page: '',
    job_title: '',
    language: 'ja' as 'ja' | 'en',
    hired_at: '',
    employment_type: 'hourly' as 'hourly' | 'monthly',
    engagement_type: 'employee' as 'employee' | 'contractor',
    settlement_type: 'monthly_payroll' as 'monthly_payroll' | 'daily_cash' | 'daily_transfer',
    accounting_entry_type: 'labor' as 'labor' | 'cost_of_sales' | 'operating_expense',
    accounting_category: '給与・賃金',
    hourly_wage_yen: '',
    monthly_salary_yen: '',
    effective_from: '',
    mynumber_submitted: false,
    passport_required: false,
    passport_submitted: false,
    passport_expiry: '',
    residence_status: '',
    residence_card_required: false,
    residence_card_submitted: false,
    residence_card_expiry: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invitationMethod, setInvitationMethod] = useState<'email' | 'sms' | 'email_and_phone'>('email');
  const [departments, setDepartments] = useState<Array<{ id: string; department_name: string }>>([]);

  useEffect(() => {
    supabase.from('staff_departments').select('id,department_name').order('sort_order').then(({ data }) => setDepartments(data || []));
  }, []);

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDepartment = (id: string) => setFormData((prev) => {
    const selected = prev.department_ids.includes(id)
      ? prev.department_ids.filter((value) => value !== id)
      : [...prev.department_ids, id];
    const primary = selected.includes(prev.department_id) ? prev.department_id : (selected[0] || '');
    return { ...prev, department_ids: selected, department_id: primary };
  });

  const togglePermission = (key: string) => setFormData((prev) => ({
    ...prev,
    permissions: prev.permissions.includes(key)
      ? prev.permissions.filter((value) => value !== key)
      : [...prev.permissions, key],
  }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setError(null);
    if (formData.access_level !== 'super_admin' && formData.department_ids.length === 0) {
      setError('運用管理者・一般スタッフには所属部門を1つ以上設定してください。');
      return;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      setError('メールアドレスまたは携帯電話番号のどちらかを入力してください。');
      return;
    }
    setSubmitting(true);

    try {
      const token = session?.access_token;
      if (!token) {
        setError('認証情報がありません。再ログインしてください。');
        setSubmitting(false);
        return;
      }

      const body: Record<string, unknown> = {
        email: formData.email.trim() || null,
        full_name: formData.full_name.trim(),
        access_level: formData.access_level,
        department_id: formData.department_id || null,
        department_ids: formData.department_ids,
        permissions: formData.access_level === 'operations_manager' ? formData.permissions : [],
        default_landing_page: formData.default_landing_page || null,
        job_title: formData.job_title.trim() || null,
        language: formData.language,
        employment_type: formData.employment_type,
        engagement_type: formData.engagement_type,
        settlement_type: formData.settlement_type,
        accounting_entry_type: formData.accounting_entry_type,
        accounting_category: formData.accounting_category.trim(),
        effective_from: formData.effective_from,
        mynumber_submitted: formData.mynumber_submitted,
        passport_required: formData.passport_required,
        passport_submitted: formData.passport_submitted,
        residence_card_required: formData.residence_card_required,
        residence_card_submitted: formData.residence_card_submitted,
      };

      if (formData.phone.trim()) body.phone = formData.phone.trim();
      if (formData.address.trim()) body.address = formData.address.trim();
      if (formData.hired_at) body.hired_at = formData.hired_at;
      if (formData.employment_type === 'hourly') {
        body.hourly_wage_yen = parseInt(formData.hourly_wage_yen, 10);
      } else {
        body.monthly_salary_yen = parseInt(formData.monthly_salary_yen, 10);
      }
      if (formData.passport_expiry) body.passport_expiry = formData.passport_expiry;
      if (formData.residence_status.trim()) body.residence_status = formData.residence_status.trim();
      if (formData.residence_card_expiry) body.residence_card_expiry = formData.residence_card_expiry;

      const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/invite-staff`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok) {
        const serverMsg = result?.error || result?.meta?.message || '';
        if (response.status === 409) {
          setError('このメールアドレスまたは電話番号は既に使用されています');
        } else if (response.status === 401) {
          setError('認証が切れています。再ログインしてください。');
        } else if (response.status === 403) {
          setError('権限がありません');
        } else if (serverMsg.includes('email') || serverMsg.includes('phone') || serverMsg.includes('メール') || serverMsg.includes('電話')) {
          setError('正しいメールアドレスまたは携帯電話番号を入力してください');
        } else if (serverMsg.includes('full_name') || serverMsg.includes('名前')) {
          setError('氏名を入力してください');
        } else if (serverMsg.includes('wage') || serverMsg.includes('salary') || serverMsg.includes('賃金') || serverMsg.includes('給与')) {
          setError('賃金情報が正しくありません');
        } else {
          setError('招待の送信に失敗しました。入力内容を確認してください。');
        }
        setSubmitting(false);
        return;
      }

      setInvitationMethod(result?.invitation_method || 'email');
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch {
      setError('通信エラーが発生しました。ネットワークを確認してください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-check-line text-accent-700 text-xl"></i>
          </span>
        </div>
        <p className="text-base font-semibold text-foreground-900">{invitationMethod === 'sms' ? 'SMS認証を送信しました' : invitationMethod === 'email_and_phone' ? 'スタッフを登録し、招待メールを送信しました' : '招待メールを送信しました'}</p>
        <p className="text-sm text-foreground-600 mt-1">{invitationMethod === 'sms' ? 'スタッフは届いた6桁のコードでログインできます' : invitationMethod === 'email_and_phone' ? '招待メールのほか、登録した電話番号でもログインできます' : 'スタッフが招待リンクからアカウントを設定できます'}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg bg-primary-50 px-3 py-2.5 text-xs text-primary-800">メールか携帯電話番号のどちらか一方だけでも招待できます。</div>
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1">メールアドレス <span className="text-xs text-foreground-500">（電話だけなら不要）</span></label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => updateField('email', e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          placeholder="staff@example.com"
        />
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1">氏名 <span className="text-red-500">*</span></label>
        <input
          type="text"
          required
          value={formData.full_name}
          onChange={(e) => updateField('full_name', e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          placeholder="山田 太郎"
        />
      </div>

      {/* Phone & Language */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">携帯電話番号 <span className="text-xs text-foreground-500">（メールだけなら不要）</span></label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
            placeholder="090-1234-5678"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">言語 <span className="text-red-500">*</span></label>
          <select
            value={formData.language}
            onChange={(e) => updateField('language', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
          >
            <option value="ja">日本語</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1">住所</label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => updateField('address', e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          placeholder="長野県白馬村..."
        />
      </div>

      {/* Department & Job title */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-2">所属部署（複数選択可） <span className="text-red-500">*</span></label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {departments.map((department) => <label key={department.id} className="flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2.5 cursor-pointer">
            <input type="checkbox" checked={formData.department_ids.includes(department.id)} onChange={() => toggleDepartment(department.id)} />
            <span className="text-sm">{department.department_name}</span>
          </label>)}
        </div>
        {formData.department_ids.length > 0 && <div className="mt-2"><label className="block text-xs text-foreground-600 mb-1">主所属</label><select value={formData.department_id} onChange={(e) => updateField('department_id', e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm">{formData.department_ids.map((id) => <option key={id} value={id}>{departments.find((d) => d.id === id)?.department_name}</option>)}</select></div>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">役割・役職</label>
          <input type="text" maxLength={100} value={formData.job_title} onChange={(e) => updateField('job_title', e.target.value)} placeholder="例：役員、マネージャー、社員、スタッフ" className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">入社日</label>
          <input type="date" value={formData.hired_at} onChange={(e) => updateField('hired_at', e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm" />
        </div>
      </div>

      <section><label className="block text-sm font-medium text-foreground-800 mb-2">システム権限 <span className="text-red-500">*</span></label><div className="space-y-2">{([
        ['super_admin','最高管理者','全事業と、給与・契約書・個人書類・損益など機密情報を含むすべて'],
        ['operations_manager','部門責任者','指定した部門の運用情報。給与・契約書・損益など機密情報は表示しません'],
        ['staff','一般スタッフ','自分の勤怠・シフトと、所属部門で担当する業務だけ'],
      ] as const).map(([value,label,description]) => <label key={value} className={`block rounded-lg border p-3 cursor-pointer ${formData.access_level === value ? 'border-primary-500 bg-primary-50' : 'border-background-200'}`}><div className="flex gap-2"><input type="radio" name="access_level" checked={formData.access_level === value} onChange={() => setFormData((prev) => ({ ...prev, access_level: value, permissions: value === 'operations_manager' ? prev.permissions : [] }))} /><div><p className="text-sm font-semibold">{label}</p><p className="text-xs text-foreground-600 mt-0.5">{description}</p></div></div></label>)}</div></section>

      {formData.access_level === 'operations_manager' && <section><label className="block text-sm font-medium text-foreground-800 mb-2">担当部門で許可する操作</label><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{permissionOptions.map(([key,label]) => <label key={key} className="flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2.5 cursor-pointer"><input type="checkbox" checked={formData.permissions.includes(key)} onChange={() => togglePermission(key)} /><span className="text-sm">{label}</span></label>)}</div></section>}

      <div><label className="block text-sm font-medium text-foreground-800 mb-1">ログイン後の開始画面</label><select value={formData.default_landing_page} onChange={(e) => updateField('default_landing_page', e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm"><option value="">全社TOP（権限に応じた事業のみ）</option><option value="/attendance">勤怠</option><option value="/shifts">シフト</option><option value="/availability">休み・勤務希望</option><option value="/purchase-requests">発注・手配</option><option value="/reimbursements">立替精算</option><option value="/casa">Casa Kokoro</option><option value="/reservations">予約管理</option><option value="/cleaning">清掃管理</option><option value="/goldys">Goldy’s 店舗業務</option><option value="/goldys-closing">Goldy’s 日次締め</option></select></div>

      {/* Employment Type & Effective From */}
      <section className="rounded-xl border border-background-200 p-4 space-y-3"><h4 className="font-semibold">契約・精算区分</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><div><label className="block text-sm font-medium mb-1">契約区分</label><select value={formData.engagement_type} onChange={(e)=>setFormData(prev=>({...prev,engagement_type:e.target.value as 'employee'|'contractor',accounting_entry_type:e.target.value==='contractor'?'cost_of_sales':'labor',accounting_category:e.target.value==='contractor'?'業務委託費':'給与・賃金'}))} className="w-full px-3.5 py-2.5 rounded-lg border bg-white"><option value="employee">雇用スタッフ</option><option value="contractor">業務委託</option></select></div><div><label className="block text-sm font-medium mb-1">精算方法</label><select value={formData.settlement_type} onChange={(e)=>updateField('settlement_type',e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border bg-white"><option value="monthly_payroll">月次給与・報酬</option><option value="daily_cash">日払い（現金・受領サイン）</option><option value="daily_transfer">日払い（振込）</option></select></div><div><label className="block text-sm font-medium mb-1">損益の集計先</label><select value={formData.accounting_entry_type} onChange={(e)=>updateField('accounting_entry_type',e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border bg-white"><option value="labor">人件費</option><option value="cost_of_sales">原価・業務委託費</option><option value="operating_expense">販売管理費</option></select></div><div><label className="block text-sm font-medium mb-1">勘定表示名</label><input required value={formData.accounting_category} onChange={(e)=>updateField('accounting_category',e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border" /></div></div><p className="text-xs text-foreground-500">業務委託は限定スタッフ権限が初期値です。シフト・勤怠・連絡・自分の精算だけ利用できます。</p></section>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">雇用形態 <span className="text-red-500">*</span></label>
          <select
            value={formData.employment_type}
            onChange={(e) => updateField('employment_type', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
          >
            <option value="hourly">時給制</option>
            <option value="monthly">月給制</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">適用開始日 <span className="text-red-500">*</span></label>
          <input
            type="date"
            required
            value={formData.effective_from}
            onChange={(e) => updateField('effective_from', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
          />
        </div>
      </div>

      {/* Wage */}
      {formData.employment_type === 'hourly' ? (
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">時給（円） <span className="text-red-500">*</span></label>
          <input
            type="number"
            required
            min="1"
            value={formData.hourly_wage_yen}
            onChange={(e) => updateField('hourly_wage_yen', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
            placeholder="1200"
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">月給（円） <span className="text-red-500">*</span></label>
          <input
            type="number"
            required
            min="1"
            value={formData.monthly_salary_yen}
            onChange={(e) => updateField('monthly_salary_yen', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
            placeholder="250000"
          />
        </div>
      )}

      {/* Document Section */}
      <div className="border-t border-background-200/70 pt-4">
        <h4 className="text-sm font-semibold text-foreground-800 mb-3">書類情報</h4>

        {/* My Number */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-foreground-700">マイナンバー提出済み</span>
          <button
            type="button"
            onClick={() => updateField('mynumber_submitted', !formData.mynumber_submitted)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
              formData.mynumber_submitted ? 'bg-accent-500' : 'bg-background-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              formData.mynumber_submitted ? 'translate-x-6' : 'translate-x-1'
            }`}></span>
          </button>
        </div>

        {/* Passport */}
        <div className="border-t border-background-200/70 pt-3 mt-3 space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-foreground-700">パスポート必要</span>
            <button
              type="button"
              onClick={() => updateField('passport_required', !formData.passport_required)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                formData.passport_required ? 'bg-accent-500' : 'bg-background-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.passport_required ? 'translate-x-6' : 'translate-x-1'
              }`}></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-foreground-700">パスポート提出済み</span>
            <button
              type="button"
              onClick={() => updateField('passport_submitted', !formData.passport_submitted)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                formData.passport_submitted ? 'bg-accent-500' : 'bg-background-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.passport_submitted ? 'translate-x-6' : 'translate-x-1'
              }`}></span>
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1">パスポート有効期限</label>
            <input
              type="date"
              value={formData.passport_expiry}
              onChange={(e) => updateField('passport_expiry', e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
            />
          </div>
        </div>

        {/* Residence */}
        <div className="border-t border-background-200/70 pt-3 mt-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1">在留資格</label>
            <input
              type="text"
              value={formData.residence_status}
              onChange={(e) => updateField('residence_status', e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
              placeholder="特定技能1号 等"
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-foreground-700">在留カード必要</span>
            <button
              type="button"
              onClick={() => updateField('residence_card_required', !formData.residence_card_required)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                formData.residence_card_required ? 'bg-accent-500' : 'bg-background-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.residence_card_required ? 'translate-x-6' : 'translate-x-1'
              }`}></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-foreground-700">在留カード提出済み</span>
            <button
              type="button"
              onClick={() => updateField('residence_card_submitted', !formData.residence_card_submitted)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                formData.residence_card_submitted ? 'bg-accent-500' : 'bg-background-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.residence_card_submitted ? 'translate-x-6' : 'translate-x-1'
              }`}></span>
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1">在留カード有効期限</label>
            <input
              type="date"
              value={formData.residence_card_expiry}
              onChange={(e) => updateField('residence_card_expiry', e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2.5 rounded-lg bg-red-50 px-3.5 py-2.5">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-px">
            <i className="ri-error-warning-line text-sm text-red-600"></i>
          </span>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          キャンセル
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin"></i>
              </span>
              送信中...
            </>
          ) : (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line text-sm"></i>
              </span>
              招待を送信
            </>
          )}
        </button>
      </div>
    </form>
  );
}
